import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Mail, Sparkles, UsersRound } from 'lucide-react';
import { getCrmEmailDetail, getCrmMembers, type CrmActivity } from '@/api/crm';
import { personName, useCrmFormat } from './crm-format';
import { hasCrmAccess } from './crm-access';
import styles from './Crm.module.css';

const list = (value: unknown) =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];

/**
 * A synced email (read live from the mailbox that synced it, with its AI
 * summary) or a calendar call (who was on the invite).
 */
export const CrmActivityDetail = ({ activity }: { activity: CrmActivity }) => {
  const { t, shortDateTime } = useCrmFormat();
  const metadata = activity.metadata ?? {};
  const isEmail = activity.kind.startsWith('email_');
  const email = useQuery({
    queryKey: ['crm', 'email', activity.id],
    queryFn: () => getCrmEmailDetail(activity.id),
    enabled: isEmail,
  });
  const members = useQuery({
    queryKey: ['crm', 'members'],
    queryFn: getCrmMembers,
  });
  const nameOf = (address: string) => {
    const member = members.data?.find(
      (m) => m.email.toLowerCase() === address.toLowerCase()
    );
    return member ? `${personName(member)} <${address}>` : address;
  };
  const people = (label: string, addresses: string[]) =>
    addresses.length > 0 && (
      <div>
        <dt>{label}</dt>
        <dd>{addresses.map(nameOf).join(', ')}</dd>
      </div>
    );
  if (!isEmail) {
    const attendees = list(metadata.attendees);
    return (
      <div className={styles.form}>
        <p className={styles.detailWhen}>
          <CalendarDays aria-hidden="true" />
          {shortDateTime(activity.occurredAt)}
        </p>
        <h3 className={styles.settingsHeading}>
          <UsersRound aria-hidden="true" />{' '}
          {t('crm.workspace.invitees', { count: attendees.length })}
        </h3>
        <ul className={styles.inviteeList}>
          {attendees.map((address) => (
            <li key={address}>
              {nameOf(address)}
              {hasCrmAccess(address) && (
                <span className={styles.chip}>{t('crm.workspace.team')}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  const summary = email.data?.summary ?? activity.summary;
  return (
    <div className={styles.form}>
      <dl className={styles.leadFacts}>
        {people(t('crm.workspace.email_from_label'), list([metadata.from]))}
        {people(t('crm.workspace.email_to_label'), list(metadata.to))}
        {people(t('crm.workspace.email_cc_label'), list(metadata.cc))}
        <div>
          <dt>{t('crm.workspace.email_date_label')}</dt>
          <dd>{shortDateTime(activity.occurredAt)}</dd>
        </div>
      </dl>
      {summary && (
        <p className={styles.emailSummary}>
          <Sparkles aria-hidden="true" />
          {summary}
        </p>
      )}
      {email.isPending ? (
        <p role="status" className={styles.loading}>
          {t('crm.loading')}
        </p>
      ) : email.data?.available ? (
        <div className={styles.emailBody}>{email.data.text}</div>
      ) : (
        <p className={styles.muted}>
          <Mail aria-hidden="true" /> {t('crm.workspace.email_unavailable')}
        </p>
      )}
    </div>
  );
};
